import React, { createContext, useContext, useEffect, useState } from "react";
import { useSSENotifications } from "../hooks/useSSENotifications";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
} from "../features/notificationApi";
import { useAuth } from "./AuthContext";

interface Notification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  is_read: boolean;
  read_at?: string;
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  error: string | null;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotificationContext must be used within NotificationProvider",
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isConnected, error, lastNotification } = useSSENotifications();

  const skipFetch = !isAuthenticated || authLoading;

  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery({ skip: 0, limit: 20 }, { skip: skipFetch });

  const { data: unreadData, refetch: refetchUnread } = useGetUnreadCountQuery(
    undefined,
    { skip: skipFetch },
  );

  const [markAsReadMutation] = useMarkAsReadMutation();
  const [markAllAsReadMutation] = useMarkAllAsReadMutation();

  const [sseNotifications, setSseNotifications] = useState<Notification[]>([]);
  const [sseUnreadDelta, setSseUnreadDelta] = useState(0);

  const baseNotifications =
    (notificationsData?.notifications as unknown as Notification[]) ?? [];
  const notifications =
    sseNotifications.length > 0
      ? [...sseNotifications, ...baseNotifications].slice(0, 20)
      : baseNotifications;

  const unreadCount = Math.max(
    0,
    (unreadData?.unread_count ?? 0) + sseUnreadDelta,
  );

  useEffect(() => {
    if (lastNotification) {
      setSseNotifications((prev) => [lastNotification, ...prev]);
      if (!lastNotification.is_read) {
        setSseUnreadDelta((prev) => prev + 1);
      }
    }
  }, [lastNotification]);

  const markAsRead = async (notificationId: string) => {
    await markAsReadMutation(notificationId).unwrap();
    setSseNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId
          ? { ...n, is_read: true, read_at: new Date().toISOString() }
          : n,
      ),
    );
  };

  const markAllAsRead = async () => {
    await markAllAsReadMutation().unwrap();
    setSseNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        is_read: true,
        read_at: new Date().toISOString(),
      })),
    );
    setSseUnreadDelta(0);
  };

  const refetch = async () => {
    setSseNotifications([]);
    setSseUnreadDelta(0);
    await Promise.all([refetchNotifications(), refetchUnread()]);
  };

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    isConnected,
    error,
    markAsRead,
    markAllAsRead,
    refetch,
    isLoading: notificationsLoading,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
